/** 实验配置状态（Zustand）：跨 配置→策略→仿真 三页共享的实验草稿。 */
import { create } from 'zustand';
import type { ExperimentConfig } from '../core/config-schema';
import type { DispatchStrategy, StrategyParams } from '../strategy/strategy-api';
import type { Rule } from '../strategy/rule-engine/rule-model';
import { createStrategy } from '../strategy/registry';
import { createRuleStrategy } from '../strategy/rule-engine/rule-strategy';

export interface ElevatorDraft {
  capacity: number;
  ratedSpeed: number;
  acceleration: number;
  doorOpenTime: number;
  doorCloseTime: number;
  boardTimePerPax: number;
  alightTimePerPax: number;
  carMass: number;
  initialFloor: number;
}

export interface SegmentDraft {
  startSec: number;
  endSec: number;
  arrivalRatePer5min: number;
}

export interface SetupFields {
  floors: number;
  floorHeight: number;
  lobbyFloor: number;
  elevatorCount: number;
  elevator: ElevatorDraft;
  segments: SegmentDraft[];
  upPeak: number;
  downPeak: number;
  interfloor: number;
  seed: number;
  strategyId: string;
  strategyParams: StrategyParams;
  rules: Rule[];
}

export interface SetupState extends SetupFields {
  patch: (p: Partial<SetupState>) => void;
  setSegment: (i: number, p: Partial<SegmentDraft>) => void;
  setRule: (i: number, p: Partial<Rule>) => void;
  addRule: () => void;
  removeRule: (i: number) => void;
}

const DEFAULT_ELEVATOR: ElevatorDraft = {
  capacity: 13,
  ratedSpeed: 2.5,
  acceleration: 1.0,
  doorOpenTime: 2000,
  doorCloseTime: 3000,
  boardTimePerPax: 800,
  alightTimePerPax: 600,
  carMass: 1500,
  initialFloor: 0,
};

export const useSetup = create<SetupState>((set) => ({
  floors: 12,
  floorHeight: 3,
  lobbyFloor: 0,
  elevatorCount: 3,
  elevator: { ...DEFAULT_ELEVATOR },
  segments: [{ startSec: 0, endSec: 3600, arrivalRatePer5min: 40 }],
  upPeak: 0.5,
  downPeak: 0.3,
  interfloor: 0.2,
  seed: 42,
  strategyId: 'nearest-car',
  strategyParams: {},
  rules: [],

  patch: (p) => set(p),
  setSegment: (i, p) =>
    set((s) => ({ segments: s.segments.map((seg, idx) => (idx === i ? { ...seg, ...p } : seg)) })),
  setRule: (i, p) => set((s) => ({ rules: s.rules.map((r, idx) => (idx === i ? { ...r, ...p } : r)) })),
  addRule: () =>
    set((s) => ({
      rules: [
        ...s.rules,
        {
          name: `规则 ${s.rules.length + 1}`,
          priority: s.rules.length + 1,
          conditions: [{ field: 'call.waitMs', op: '>', value: 60000 }],
          action: { type: 'assign-nearest' },
        },
      ],
    })),
  removeRule: (i) => set((s) => ({ rules: s.rules.filter((_, idx) => idx !== i) })),
}));

/** 从 store 状态抽取纯数据字段（供 F-003 序列化/持久化）。 */
export function pickSetup(s: SetupFields): SetupFields {
  return {
    floors: s.floors, floorHeight: s.floorHeight, lobbyFloor: s.lobbyFloor, elevatorCount: s.elevatorCount,
    elevator: s.elevator, segments: s.segments, upPeak: s.upPeak, downPeak: s.downPeak, interfloor: s.interfloor,
    seed: s.seed, strategyId: s.strategyId, strategyParams: s.strategyParams, rules: s.rules,
  };
}

/** 由草稿构建 ExperimentConfig。 */
export function buildExperimentConfig(s: SetupFields): ExperimentConfig {
  return {
    seed: s.seed,
    scene: {
      floors: s.floors,
      floorHeight: s.floorHeight,
      lobbyFloor: s.lobbyFloor,
      elevators: Array.from({ length: s.elevatorCount }, (_, i) => ({ id: `c${i}`, ...s.elevator })),
    },
    traffic: {
      profile: s.segments.map((seg) => ({
        startSec: seg.startSec,
        endSec: seg.endSec,
        arrivalRatePer5min: seg.arrivalRatePer5min,
      })),
      mode: { upPeak: s.upPeak, downPeak: s.downPeak, interfloor: s.interfloor },
    },
  };
}

/** 由草稿构建策略工厂（含参数应用）。 */
export function buildStrategyFactory(s: SetupFields): () => DispatchStrategy {
  return () => {
    const strat =
      s.strategyId === 'rule-engine' ? createRuleStrategy(s.rules) : createStrategy(s.strategyId);
    if (Object.keys(s.strategyParams).length > 0) strat.setParams?.(s.strategyParams);
    return strat;
  };
}
